import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Btn, Card, Field, Input, Select, Textarea, PageTitle, StatusChip, DemoBadge, Tag, Meta, Notice, ErrorSummary, SourceTag, cx } from "../components/kit";
import { NoAccess, Loading, HistoryList } from "../components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { BaselineEditor, BaselineView } from "../components/Baseline";
import { FileThumb, AuthImage } from "../components/FileUploader";
import { api, parseError } from "../lib/api";
import { fmtDate, fmtDateTime } from "../lib/format";
import { EntriesPanel } from "./DogEntries";
import { AccessPanel } from "./DogAccess";

export default function DogDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("overview");
  const [denied, setDenied] = useState(false);
  const load = useCallback(() => api.get(`/dogs/${id}`).then((r) => setD(r.data)).catch((e) => parseError(e).status === 404 && setDenied(true)), [id]);
  useEffect(() => { load(); }, [load]);
  if (denied) return <NoAccess />;
  if (!d) return <Loading />;
  const dog = d.dog;
  const tabs = ["overview", "observations", "feasibility", "restrictions", "hypotheses", "files", "programmes", "access", "history"];
  return (
    <div>
      <PageTitle title={dog.name} badge={<>{dog.is_demo && <DemoBadge />}<Tag>{dog.number}</Tag><StatusChip status={dog.status} /></>}
        sub={<span>{t("common.responsible")}: <b>{dog.responsible_name}</b></span>} />
      <div role="tablist" className="mb-5 flex gap-1 overflow-x-auto pb-1">
        {tabs.map((k) => (
          <button key={k} role="tab" aria-selected={tab === k} data-testid={`dog-tab-${k}`} onClick={() => setTab(k)}
            className={cx("min-h-[44px] whitespace-nowrap rounded-xl border px-3.5 text-sm font-semibold", tab === k ? "border-primary bg-white" : "border-transparent text-muted-foreground hover:bg-white")}>{t(`dog.tab_${k}`)}</button>
        ))}
      </div>
      {tab === "overview" && <Overview d={d} reload={load} />}
      {["observations", "feasibility", "restrictions", "hypotheses"].includes(tab) && <EntriesPanel type={tab} d={d} reload={load} />}
      {tab === "files" && <FilesTab files={d.files} />}
      {tab === "programmes" && <ProgrammesTab d={d} />}
      {tab === "access" && <AccessPanel dogId={id} />}
      {tab === "history" && <DogHistory id={id} dog={dog} />}
    </div>
  );
}

function Overview({ d, reload }) {
  const { t } = useTranslation();
  const dog = d.dog;
  const [edit, setEdit] = useState(null);
  const save = async () => {
    try {
      await api.patch(`/dogs/${dog.id}`, edit);
      toast.success(t("common.saved"));
      setEdit(null);
      reload();
    } catch (e) { toast.error(t(`err.${parseError(e).code}`)); }
  };
  return (
    <div className="space-y-5">
      <Card>
        <div className="mb-4 flex items-center justify-between"><h2 className="text-lg font-bold">{t("group.identification")}</h2>
          {!edit && <Btn variant="outline" testid="dog-edit-btn" onClick={() => setEdit({ baseline: dog.baseline, referral_source: dog.referral_source, breed: dog.breed, owner_email: dog.owner_email, owner_name: dog.owner_name })}>{t("common.edit")}</Btn>}</div>
        <dl className="grid gap-4 sm:grid-cols-3">
          <Meta label={t("field.species")}>{t(`species.${dog.species}`)}</Meta>
          <Meta label={t("field.breed")} testid="dog-breed-value">{dog.breed || <i className="text-muted-foreground">{t("common.unknown")}</i>}</Meta>
          <Meta label={t("field.sex")}>{t(`sex.${dog.sex || "unknown"}`)}</Meta>
          <Meta label={t("field.age_range")}>{dog.birth_date ? fmtDate(dog.birth_date) : dog.age_range || <i className="text-muted-foreground">{t("common.unknown")}</i>}</Meta>
          <Meta label={t("field.owner_name")}>{dog.owner_name}</Meta>
          <Meta label={t("field.owner_email")}>{dog.owner_email}</Meta>
          <Meta label={t("field.referral_source")}>{dog.referral_source}</Meta>
          <Meta label={t("common.created")}>{fmtDateTime(dog.created_at)} · {dog.creator_name}</Meta>
          <Meta label={t("common.workspace_record")}>{dog.number}</Meta>
        </dl>
        {edit && (
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <Field label={t("field.breed")} htmlFor="e-breed"><Input id="e-breed" data-testid="dog-edit-breed-input" value={edit.breed} onChange={(e) => setEdit({ ...edit, breed: e.target.value })} /></Field>
            <Field label={t("field.referral_source")} htmlFor="e-ref"><Input id="e-ref" data-testid="dog-edit-referral-input" value={edit.referral_source} onChange={(e) => setEdit({ ...edit, referral_source: e.target.value })} /></Field>
          </div>
        )}
      </Card>
      {["coat", "history", "circumstances"].map((g) => (
        <Card key={g}>
          <h2 className="mb-3 text-lg font-bold">{t(`group.${g}`)}</h2>
          {edit ? <BaselineEditor group={g} value={edit.baseline[g]} onChange={(v) => setEdit({ ...edit, baseline: { ...edit.baseline, [g]: v } })} /> : <BaselineView group={g} value={dog.baseline?.[g]} />}
        </Card>
      ))}
      {edit && <div className="flex gap-2"><Btn testid="dog-edit-save-btn" onClick={save}>{t("common.save_changes")}</Btn><Btn variant="ghost" onClick={() => setEdit(null)}>{t("common.back")}</Btn></div>}
    </div>
  );
}

function FilesTab({ files }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(null);
  if (!files.length) return <p className="text-muted-foreground">{t("files.empty")}</p>;
  return (
    <>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6" data-testid="dog-files-grid">{files.map((f) => <FileThumb key={f.id} file={f} onOpen={setOpen} />)}</div>
      <Dialog open={!!open} onOpenChange={() => setOpen(null)}>
        <DialogContent className="max-w-3xl rounded-2xl" data-testid="file-viewer">
          {open && (<>
            <DialogHeader><DialogTitle>{open.title}</DialogTitle></DialogHeader>
            {open.kind === "photo" ? <AuthImage fileId={open.id} alt={open.title} contain className="max-h-[60vh] w-full rounded-xl" testid="file-viewer-image" /> : <p>{t("files.video")}</p>}
            <dl className="grid grid-cols-2 gap-3 text-sm">
              <Meta label={t("files.author")}>{open.author_name}</Meta><Meta label={t("files.added")}>{fmtDateTime(open.added_at)}</Meta>
              <Meta label={t("files.capture")}>{open.capture_date ? fmtDate(open.capture_date) : t("common.unknown")}</Meta>
              <Meta label={t("field.source")}><SourceTag source={open.source} /></Meta><Meta label={t("files.visibility")}>{t(`visibility.${open.visibility}`)}</Meta>
              <Meta label={t("field.region")}>{open.region ? t(`region.${open.region}`) : "—"}</Meta>
            </dl>
          </>)}
        </DialogContent>
      </Dialog>
    </>
  );
}

function ProgrammesTab({ d }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <div className="space-y-3">
      <Btn testid="start-programme-btn" onClick={() => setOpen(true)}>{t("programme.start")}</Btn>
      {d.programmes.length === 0 && <p className="text-muted-foreground">{t("programme.none")}</p>}
      <ul className="grid gap-3">
        {d.programmes.map((p) => (
          <li key={p.id}><Link to={`/programmes/${p.id}`} data-testid={`dog-programme-${p.number}`} className="lift flex flex-wrap items-center gap-2 rounded-2xl border bg-white p-4">
            <b>{p.title || p.number}</b><Tag>{p.number}</Tag>{p.is_demo && <DemoBadge short />}{p.archived && <StatusChip status="archived" label={t("status.archived")} />}
            <span className="text-sm text-muted-foreground">{p.directions.join(" · ")}</span><span className="ml-auto"><StatusChip status={p.status} /></span>
          </Link></li>
        ))}
      </ul>
      <NewProgrammeDialog open={open} onOpenChange={setOpen} dogId={d.dog.id} />
    </div>
  );
}

function NewProgrammeDialog({ open, onOpenChange, dogId }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [pre, setPre] = useState(null);
  const [f, setF] = useState({ title: "", objective: "", directions: [] });
  const [sim, setSim] = useState(null);
  const [reason, setReason] = useState("");
  const [apiErr, setApiErr] = useState(null);
  useEffect(() => { if (open) api.get(`/dogs/${dogId}/precheck`).then((r) => setPre(r.data)); }, [open, dogId]);
  const toggle = (x) => setF({ ...f, directions: f.directions.includes(x) ? f.directions.filter((y) => y !== x) : [...f.directions, x] });
  const create = async () => {
    setApiErr(null);
    try {
      const { data } = await api.post("/programmes", { ...f, dog_id: dogId, similar_reason: sim ? reason : undefined });
      navigate(`/programmes/${data.id}`);
    } catch (e) {
      const p = parseError(e);
      if (p.code === "similar_active") setSim(p.programmes); else setApiErr(p);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="new-programme-dialog" className="max-h-[90vh] overflow-y-auto rounded-2xl sm:max-w-2xl">
        <DialogHeader><DialogTitle className="font-display text-xl">{t("programme.new_title")}</DialogTitle></DialogHeader>
        {pre && (pre.missing.length > 0 || pre.restrictions.length > 0) && (
          <div className="space-y-2" data-testid="programme-precheck">
            {pre.missing.length > 0 && <Notice tone="warn" testid="precheck-missing"><b>{t("programme.missing_baseline")}</b><ul className="mt-1 list-disc pl-5">{pre.missing.map((m) => <li key={m}>{t(`missing.${m}`)}</li>)}</ul></Notice>}
            {pre.restrictions.length > 0 && <Notice tone="warn" testid="precheck-restrictions"><b>{t("programme.current_restrictions")}</b><ul className="mt-1 list-disc pl-5">{pre.restrictions.map((r) => <li key={r.id}>{r.text}</li>)}</ul></Notice>}
            <p className="text-sm text-muted-foreground">{t("programme.draft_still_possible")}</p>
          </div>
        )}
        <ErrorSummary errors={apiErr?.errors} />
        <Field label={t("field.title")} htmlFor="np-title"><Input id="np-title" data-testid="new-programme-title-input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></Field>
        <Field label={t("field.objective")} htmlFor="np-obj"><Textarea id="np-obj" data-testid="new-programme-objective-input" value={f.objective} onChange={(e) => setF({ ...f, objective: e.target.value })} /></Field>
        <fieldset>
          <legend className="mb-2 text-sm font-semibold">{t("programme.directions")} ({t("common.required")})</legend>
          <div className="grid gap-2">
            {["A", "B", "C", "D"].map((x) => (
              <label key={x} className={cx("flex min-h-[48px] items-center gap-3 rounded-xl border bg-white px-3", f.directions.includes(x) && "border-primary bg-[hsl(var(--peach))]")}>
                <input type="checkbox" className="h-5 w-5" data-testid={`direction-checkbox-${x}`} checked={f.directions.includes(x)} onChange={() => toggle(x)} />
                <b>{x}</b> — {t(`dir.${x}`)}
              </label>
            ))}
          </div>
        </fieldset>
        {sim && (
          <Notice tone="warn" testid="similar-programme-warning">
            <b>{t("programme.similar_title")}</b>
            <ul className="my-2 space-y-1">{sim.map((p) => <li key={p.id} className="flex flex-wrap items-center gap-2">{p.number} — {p.title}<Link to={`/programmes/${p.id}`}><Btn variant="outline" className="min-h-[36px]" testid="similar-continue-existing-btn">{t("programme.continue_existing")}</Btn></Link></li>)}</ul>
            <Field label={t("programme.similar_reason")} htmlFor="sim-r" required><Textarea id="sim-r" data-testid="similar-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
          </Notice>
        )}
        <Btn testid="new-programme-create-btn" disabled={sim && !reason.trim()} onClick={create}>{t("programme.create_draft")}</Btn>
      </DialogContent>
    </Dialog>
  );
}

function DogHistory({ id, dog }) {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get(`/dogs/${id}/history`).then((r) => setItems(r.data)); }, [id]);
  return items ? <HistoryList items={items} testid="dog-history" /> : <Loading />;
}
